export interface QuizData {
  industry: string
  industryOther?: string
  isOwner: string
  state: string
  yearsInBusiness: string
  revenue: string
  profit: string
  locations: string
  providers: string
  payerMix: string
  concentration: string
  trend: string
  timeline: string
  ownerHours: string
}

export interface ValuationResult {
  low: number
  high: number
  confidence: 'Low' | 'Medium' | 'High'
  valueUnlocks: string[]
  qualityScore: number
  multipleRange: [number, number]
  ebitda: number
  keyManRisk: 'High' | 'Moderate' | 'Low'
}

const REVENUE_MIDPOINTS: Record<string, number> = {
  '0-1M': 500000,
  '1-3M': 2000000,
  '3-5M': 4000000,
  '5-10M': 7500000,
  '10-20M': 15000000,
}

const PROFIT_PERCENTAGES: Record<string, number> = {
  '0-10': 0.05,
  '10-15': 0.125,
  '15-25': 0.2,
  '25+': 0.3,
  'not-sure': 0.12,
}

export function calculateValuation(data: QuizData): ValuationResult {
  // Revenue midpoint
  const revenueMidpoint = REVENUE_MIDPOINTS[data.revenue] || 750000

  // EBITDA estimation
  const profitPct = PROFIT_PERCENTAGES[data.profit] || 0.12
  const ebitda = revenueMidpoint * profitPct

  // Route to industry-specific calculation
  switch (data.industry) {
    case 'chiropractic':
      return calculateChiropracticValuation(data, ebitda, revenueMidpoint)
    case 'dental':
      return calculateDentalValuation(data, ebitda, revenueMidpoint)
    case 'medspa':
      return calculateMedSpaValuation(data, ebitda, revenueMidpoint)
    case 'physical-therapy':
      return calculatePhysicalTherapyValuation(data, ebitda, revenueMidpoint)
    case 'other':
      return calculateOtherValuation(data, ebitda, revenueMidpoint)
    default:
      // Default to dental if industry not specified
      return calculateDentalValuation(data, ebitda, revenueMidpoint)
  }
}

function calculateChiropracticValuation(
  data: QuizData,
  ebitda: number,
  revenueMidpoint: number,
): ValuationResult {
  // CHIROPRACTIC: 2.5× – 3.5× EBITDA
  let baseMultiple = 3.0 // Midpoint of range

  // Key Man Risk Adjustment
  let keyManRisk: 'High' | 'Moderate' | 'Low' = 'Moderate'
  if (data.ownerHours === '40+') {
    baseMultiple -= 0.5
    keyManRisk = 'High'
  } else if (data.ownerHours === '20-40') {
    baseMultiple -= 0.25
    keyManRisk = 'Moderate'
  } else if (data.ownerHours === '<20') {
    baseMultiple += 0.2
    keyManRisk = 'Low'
  } else if (data.ownerHours === '0') {
    baseMultiple += 0.3
    keyManRisk = 'Low'
  }

  // Single location + single provider penalty
  if (data.locations === '1' && data.providers === '1') {
    baseMultiple -= 0.4
  }

  // Multi-location bonus
  if (data.locations === '2-3') {
    baseMultiple += 0.1
  } else if (['4-5', '6+'].includes(data.locations)) {
    baseMultiple += 0.2
  }

  // Large provider team bonus
  if (data.providers === '5+') {
    baseMultiple += 0.15
  }

  // Growth bonus
  if (['up-10-20', 'up-20+'].includes(data.trend)) {
    baseMultiple += 0.1
  }

  // Diversified payer mix bonus (more cash pay is better for chiro)
  if (['100-cash', '0-25-insurance'].includes(data.payerMix)) {
    baseMultiple += 0.15
  } else if (data.payerMix === '25-50-insurance') {
    baseMultiple += 0.1
  }

  // No concentration risk bonus
  if (data.concentration === 'no') {
    baseMultiple += 0.1
  }

  // Strong profit margins bonus
  if (['15-25', '25+'].includes(data.profit)) {
    baseMultiple += 0.1
  }

  // Calculate multiple range - Constrained to 2.5× – 3.5×
  const lowMultiple = Math.max(2.5, baseMultiple - 0.3)
  const highMultiple = Math.min(3.5, baseMultiple + 0.3)

  const low = Math.round(ebitda * lowMultiple)
  const high = Math.round(ebitda * highMultiple)

  const qualityScore = calculateQualityScore(data)
  const confidence = calculateConfidence(qualityScore, data.profit)
  const valueUnlocks = generateValueUnlocks(data, keyManRisk, qualityScore)

  return {
    low,
    high,
    confidence,
    valueUnlocks,
    qualityScore,
    multipleRange: [lowMultiple, highMultiple],
    ebitda,
    keyManRisk,
  }
}

function calculateDentalValuation(
  data: QuizData,
  ebitda: number,
  revenueMidpoint: number,
): ValuationResult {
  // DENTAL: 3.5× – 5.0× EBITDA (conservative, avoiding DSO premium)
  let baseMultiple = 4.0 // Midpoint of range

  // Key Man Risk Adjustment
  let keyManRisk: 'High' | 'Moderate' | 'Low' = 'Moderate'
  if (data.ownerHours === '40+') {
    baseMultiple -= 0.8
    keyManRisk = 'High'
  } else if (data.ownerHours === '20-40') {
    baseMultiple -= 0.4
    keyManRisk = 'Moderate'
  } else if (data.ownerHours === '<20') {
    baseMultiple += 0.3
    keyManRisk = 'Low'
  } else if (data.ownerHours === '0') {
    baseMultiple += 0.5
    keyManRisk = 'Low'
  }

  // Single location + single provider penalty
  if (data.locations === '1' && data.providers === '1') {
    baseMultiple -= 0.6
  }

  // Multi-location bonus
  if (data.locations === '2-3') {
    baseMultiple += 0.2
  } else if (['4-5', '6+'].includes(data.locations)) {
    baseMultiple += 0.4
  }

  // Large provider team bonus
  if (data.providers === '5+') {
    baseMultiple += 0.3
  }

  // Growth bonus
  if (['up-10-20', 'up-20+'].includes(data.trend)) {
    baseMultiple += 0.2
  }

  // Diversified payer mix bonus (balanced mix preferred for dental)
  if (['25-50-insurance', '50-75-insurance'].includes(data.payerMix)) {
    baseMultiple += 0.15
  } else if (['0-25-insurance', '100-cash'].includes(data.payerMix)) {
    baseMultiple += 0.1
  }

  // No concentration risk bonus
  if (data.concentration === 'no') {
    baseMultiple += 0.15
  }

  // Strong profit margins bonus
  if (['15-25', '25+'].includes(data.profit)) {
    baseMultiple += 0.2
  }

  // Calculate multiple range - Constrained to 3.5× – 5.0×
  const lowMultiple = Math.max(3.5, baseMultiple - 0.4)
  const highMultiple = Math.min(5.0, baseMultiple + 0.4)

  const low = Math.round(ebitda * lowMultiple)
  const high = Math.round(ebitda * highMultiple)

  const qualityScore = calculateQualityScore(data)
  const confidence = calculateConfidence(qualityScore, data.profit)
  const valueUnlocks = generateValueUnlocks(data, keyManRisk, qualityScore)

  return {
    low,
    high,
    confidence,
    valueUnlocks,
    qualityScore,
    multipleRange: [lowMultiple, highMultiple],
    ebitda,
    keyManRisk,
  }
}

function calculateMedSpaValuation(
  data: QuizData,
  ebitda: number,
  revenueMidpoint: number,
): ValuationResult {
  // MED SPA: 4.0× – 6.0× EBITDA (avoiding 7-10× outliers)
  let baseMultiple = 5.0 // Midpoint of range

  // Key Man Risk Adjustment
  let keyManRisk: 'High' | 'Moderate' | 'Low' = 'Moderate'
  if (data.ownerHours === '40+') {
    baseMultiple -= 0.9
    keyManRisk = 'High'
  } else if (data.ownerHours === '20-40') {
    baseMultiple -= 0.5
    keyManRisk = 'Moderate'
  } else if (data.ownerHours === '<20') {
    baseMultiple += 0.4
    keyManRisk = 'Low'
  } else if (data.ownerHours === '0') {
    baseMultiple += 0.6
    keyManRisk = 'Low'
  }

  // Single location + single provider penalty
  if (data.locations === '1' && data.providers === '1') {
    baseMultiple -= 0.7
  }

  // Multi-location bonus
  if (data.locations === '2-3') {
    baseMultiple += 0.3
  } else if (['4-5', '6+'].includes(data.locations)) {
    baseMultiple += 0.5
  }

  // Large provider team bonus
  if (data.providers === '5+') {
    baseMultiple += 0.4
  }

  // Growth bonus (important for med spa)
  if (['up-10-20', 'up-20+'].includes(data.trend)) {
    baseMultiple += 0.3
  }

  // Cash-heavy payer mix bonus (med spas strongly prefer cash)
  if (data.payerMix === '100-cash') {
    baseMultiple += 0.25
  } else if (data.payerMix === '0-25-insurance') {
    baseMultiple += 0.2
  } else if (data.payerMix === '25-50-insurance') {
    baseMultiple += 0.1
  }

  // No concentration risk bonus
  if (data.concentration === 'no') {
    baseMultiple += 0.2
  }

  // Strong profit margins bonus
  if (['15-25', '25+'].includes(data.profit)) {
    baseMultiple += 0.25
  }

  // Calculate multiple range - Constrained to 4.0× – 6.0×
  const lowMultiple = Math.max(4.0, baseMultiple - 0.5)
  const highMultiple = Math.min(6.0, baseMultiple + 0.5)

  const low = Math.round(ebitda * lowMultiple)
  const high = Math.round(ebitda * highMultiple)

  const qualityScore = calculateQualityScore(data)
  const confidence = calculateConfidence(qualityScore, data.profit)
  const valueUnlocks = generateValueUnlocks(data, keyManRisk, qualityScore)

  return {
    low,
    high,
    confidence,
    valueUnlocks,
    qualityScore,
    multipleRange: [lowMultiple, highMultiple],
    ebitda,
    keyManRisk,
  }
}

function calculatePhysicalTherapyValuation(
  data: QuizData,
  ebitda: number,
  revenueMidpoint: number,
): ValuationResult {
  // PHYSICAL THERAPY: 3.0× – 5.0× EBITDA
  let baseMultiple = 4.0 // Midpoint of range

  // Key Man Risk Adjustment
  let keyManRisk: 'High' | 'Moderate' | 'Low' = 'Moderate'
  if (data.ownerHours === '40+') {
    baseMultiple -= 0.8
    keyManRisk = 'High'
  } else if (data.ownerHours === '20-40') {
    baseMultiple -= 0.4
    keyManRisk = 'Moderate'
  } else if (data.ownerHours === '<20') {
    baseMultiple += 0.3
    keyManRisk = 'Low'
  } else if (data.ownerHours === '0') {
    baseMultiple += 0.5
    keyManRisk = 'Low'
  }

  // Single location + single provider penalty
  if (data.locations === '1' && data.providers === '1') {
    baseMultiple -= 0.6
  }

  // Multi-location bonus
  if (data.locations === '2-3') {
    baseMultiple += 0.2
  } else if (['4-5', '6+'].includes(data.locations)) {
    baseMultiple += 0.4
  }

  // Large provider team bonus
  if (data.providers === '5+') {
    baseMultiple += 0.3
  }

  // Growth bonus
  if (['up-10-20', 'up-20+'].includes(data.trend)) {
    baseMultiple += 0.2
  }

  // Diversified payer mix bonus (PT typically insurance-heavy, balanced is good)
  if (['25-50-insurance', '50-75-insurance'].includes(data.payerMix)) {
    baseMultiple += 0.15
  } else if (data.payerMix === '75-100-insurance') {
    baseMultiple += 0.1 // Expected for PT, slight bonus
  } else if (data.payerMix === '100-insurance') {
    baseMultiple += 0.05 // 100% insurance is risky even for PT
  }

  // No concentration risk bonus
  if (data.concentration === 'no') {
    baseMultiple += 0.15
  }

  // Strong profit margins bonus
  if (['15-25', '25+'].includes(data.profit)) {
    baseMultiple += 0.2
  }

  // Calculate multiple range - Constrained to 3.0× – 5.0×
  const lowMultiple = Math.max(3.0, baseMultiple - 0.4)
  const highMultiple = Math.min(5.0, baseMultiple + 0.4)

  const low = Math.round(ebitda * lowMultiple)
  const high = Math.round(ebitda * highMultiple)

  const qualityScore = calculateQualityScore(data)
  const confidence = calculateConfidence(qualityScore, data.profit)
  const valueUnlocks = generateValueUnlocks(data, keyManRisk, qualityScore)

  return {
    low,
    high,
    confidence,
    valueUnlocks,
    qualityScore,
    multipleRange: [lowMultiple, highMultiple],
    ebitda,
    keyManRisk,
  }
}

function calculateOtherValuation(
  data: QuizData,
  ebitda: number,
  revenueMidpoint: number,
): ValuationResult {
  // OTHER: Conservative 3.0× – 4.5× EBITDA range
  let baseMultiple = 3.5 // Conservative midpoint

  // Key Man Risk Adjustment
  let keyManRisk: 'High' | 'Moderate' | 'Low' = 'Moderate'
  if (data.ownerHours === '40+') {
    baseMultiple -= 0.7
    keyManRisk = 'High'
  } else if (data.ownerHours === '20-40') {
    baseMultiple -= 0.35
    keyManRisk = 'Moderate'
  } else if (data.ownerHours === '<20') {
    baseMultiple += 0.25
    keyManRisk = 'Low'
  } else if (data.ownerHours === '0') {
    baseMultiple += 0.4
    keyManRisk = 'Low'
  }

  // Single location + single provider penalty
  if (data.locations === '1' && data.providers === '1') {
    baseMultiple -= 0.5
  }

  // Multi-location bonus
  if (data.locations === '2-3') {
    baseMultiple += 0.15
  } else if (['4-5', '6+'].includes(data.locations)) {
    baseMultiple += 0.3
  }

  // Large provider team bonus
  if (data.providers === '5+') {
    baseMultiple += 0.25
  }

  // Growth bonus
  if (['up-10-20', 'up-20+'].includes(data.trend)) {
    baseMultiple += 0.15
  }

  // Diversified payer mix bonus (balanced preferred for unknown industries)
  if (['25-50-insurance', '50-75-insurance'].includes(data.payerMix)) {
    baseMultiple += 0.1
  }

  // No concentration risk bonus
  if (data.concentration === 'no') {
    baseMultiple += 0.1
  }

  // Strong profit margins bonus
  if (['15-25', '25+'].includes(data.profit)) {
    baseMultiple += 0.15
  }

  // Calculate multiple range - Constrained to 3.0× – 4.5×
  const lowMultiple = Math.max(3.0, baseMultiple - 0.3)
  const highMultiple = Math.min(4.5, baseMultiple + 0.3)

  const low = Math.round(ebitda * lowMultiple)
  const high = Math.round(ebitda * highMultiple)

  const qualityScore = calculateQualityScore(data)
  const confidence = calculateConfidence(qualityScore, data.profit)
  const valueUnlocks = generateValueUnlocks(data, keyManRisk, qualityScore)

  return {
    low,
    high,
    confidence,
    valueUnlocks,
    qualityScore,
    multipleRange: [lowMultiple, highMultiple],
    ebitda,
    keyManRisk,
  }
}

function calculateQualityScore(data: QuizData): number {
  let qualityScore = 0
  if (['up-10-20', 'up-20+'].includes(data.trend)) qualityScore++
  if (['2-3', '4-5', '6+'].includes(data.locations)) qualityScore++
  if (['5+'].includes(data.providers)) qualityScore++
  if (data.concentration === 'no') qualityScore++
  if (
    [
      '100-cash',
      '0-25-insurance',
      '25-50-insurance',
      '50-75-insurance',
    ].includes(data.payerMix)
  )
    qualityScore++
  if (['15-25', '25+'].includes(data.profit)) qualityScore++
  if (['<20', '0'].includes(data.ownerHours)) qualityScore++
  return qualityScore
}

function calculateConfidence(
  qualityScore: number,
  profit: string,
): 'Low' | 'Medium' | 'High' {
  const hasNotSure = profit === 'not-sure'
  if (qualityScore >= 5 && !hasNotSure) return 'High'
  if (qualityScore <= 2 || hasNotSure) return 'Low'
  return 'Medium'
}

function generateValueUnlocks(
  data: QuizData,
  keyManRisk: 'High' | 'Moderate' | 'Low',
  qualityScore: number,
): string[] {
  const unlocks: string[] = []

  // Key man risk unlock (PRIORITY)
  if (keyManRisk === 'High') {
    unlocks.push(
      'Reduce your clinical hours and train associates to add $200K–$500K in value',
    )
  } else if (keyManRisk === 'Moderate') {
    unlocks.push(
      'Delegate more patient care to associates to increase your multiple by 0.5x',
    )
  }

  // Concentration fix
  if (data.concentration === 'yes') {
    unlocks.push('Reduce single-source risk to unlock a higher multiple')
  }

  // Margin lift
  if (['0-10', '10-15'].includes(data.profit)) {
    unlocks.push('A 5-point margin improvement could add $150K–$400K in value')
  }

  // Growth unlock
  if (['flat', 'down'].includes(data.trend)) {
    unlocks.push('Show 12 months of growth to increase buyer confidence')
  }

  // Scale unlock - Industry-specific ranges
  if (data.locations === '1') {
    let range = '3.5x–4.5x' // default
    switch (data.industry) {
      case 'chiropractic':
        range = '3.0x–3.5x'
        break
      case 'dental':
        range = '4.5x–5.0x'
        break
      case 'medspa':
        range = '5.0x–6.0x'
        break
      case 'physical-therapy':
        range = '4.0x–5.0x'
        break
    }
    unlocks.push(`Multi-location clinics command premium multiples (${range})`)
  }

  // Financial planning unlock
  unlocks.push(
    'Proper financial planning and documentation can increase multiples by 1.0x–1.5x',
  )

  // Return top 3
  return unlocks.slice(0, 3)
}
